// Lightweight, privacy-cheap analytics (Phase 10). Every event is kept in a
// capped local ring buffer so the game can show the player their own history,
// and — only when a collection endpoint is configured at build time — a copy is
// beaconed out. With no endpoint set (the default), nothing leaves the browser.
//
// The doc names eight things worth measuring; each has a helper here so the call
// sites in App read as the metric they feed rather than as a generic `track`:
//   first death · number of loops · Day 30 survival · ability progression ·
//   faction completion · battle failure rates · longest survival · completion.

export interface AnalyticsEvent {
  event: string
  props: Record<string, unknown>
  at: number
}

const ANALYTICS_KEY = "echoesBeforeEternity.analytics"
const MAX_EVENTS = 1000

function read(): AnalyticsEvent[] {
  try {
    const raw = localStorage.getItem(ANALYTICS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? (parsed as AnalyticsEvent[]) : []
  } catch {
    return []
  }
}

function write(events: AnalyticsEvent[]): void {
  try {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(events.slice(-MAX_EVENTS)))
  } catch {
    // Analytics must never be able to break the game it is measuring.
  }
}

/** Set VITE_ANALYTICS_ENDPOINT at build time to forward events; unset = local only. */
function endpoint(): string | undefined {
  try {
    return import.meta.env?.VITE_ANALYTICS_ENDPOINT
  } catch {
    return undefined
  }
}

export function track(event: string, props: Record<string, unknown> = {}): void {
  const entry: AnalyticsEvent = { event, props, at: Date.now() }
  write([...read(), entry])

  const url = endpoint()
  if (url && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      navigator.sendBeacon(url, JSON.stringify(entry))
    } catch {
      // A failed beacon is not worth a thrown error; the local copy stands.
    }
  }
}

// --- the eight named metrics, each a self-documenting call site ---------------

export function trackLoopStarted(loopNumber: number, reason: string): void {
  track("loop_started", { loop: loopNumber, reason })
}

export function trackDeath(loopNumber: number, day: number, totalDeaths: number, cause: string): void {
  if (totalDeaths === 1) track("first_death", { loop: loopNumber, day })
  track("death", { loop: loopNumber, day, totalDeaths, cause })
}

export function trackDay30Survival(loopNumber: number): void {
  track("day30_survival", { loop: loopNumber })
}

export function trackAbilityProgression(abilityId: string, level: string): void {
  track("ability_progression", { ability: abilityId, level })
}

export function trackFactionCompletion(factionId: string, kind: string): void {
  track("faction_completion", { faction: factionId, kind })
}

export function trackBattleResolved(battleId: string, outcome: string): void {
  track("battle_resolved", { battle: battleId, outcome })
}

export function trackLongestSurvival(days: number): void {
  track("longest_survival", { days })
}

export function trackGameCompleted(loopNumber: number, day: number, totalDeaths: number): void {
  track("game_completed", { loop: loopNumber, day, totalDeaths })
}

// --- read side ----------------------------------------------------------------

export function getAnalyticsEvents(): AnalyticsEvent[] {
  return read()
}

export function clearAnalytics(): void {
  try {
    localStorage.removeItem(ANALYTICS_KEY)
  } catch {
    // ignore
  }
}

export interface AnalyticsSummary {
  loops: number
  firstDeathRecorded: boolean
  deaths: number
  day30Survivals: number
  abilityProgressions: number
  factionCompletions: number
  battlesFought: number
  battlesLost: number
  battleFailureRate: number
  longestSurvival: number
  completions: number
}

/** Rolls the raw event log up into the dashboard the Progress screen shows. */
export function getAnalyticsSummary(): AnalyticsSummary {
  const events = read()
  const count = (name: string) => events.filter((e) => e.event === name).length
  const battlesFought = count("battle_resolved")
  const battlesLost = events.filter(
    (e) => e.event === "battle_resolved" && e.props.outcome === "defeat",
  ).length
  const longestSurvival = events
    .filter((e) => e.event === "longest_survival")
    .reduce((max, e) => Math.max(max, Number(e.props.days) || 0), 0)

  return {
    loops: count("loop_started"),
    firstDeathRecorded: count("first_death") > 0,
    deaths: count("death"),
    day30Survivals: count("day30_survival"),
    abilityProgressions: count("ability_progression"),
    factionCompletions: count("faction_completion"),
    battlesFought,
    battlesLost,
    battleFailureRate: battlesFought === 0 ? 0 : battlesLost / battlesFought,
    longestSurvival,
    completions: count("game_completed"),
  }
}
